export interface OrderStatusMetadata {
    label: string;
    severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';
    tooltip: string;
}

const STATUS_METADATA: Record<string, OrderStatusMetadata> = {
    DRAFT: { label: 'Draft', severity: 'info', tooltip: 'Editable draft order.' },
    PENDING_APPROVAL: { label: 'Pending approval', severity: 'warn', tooltip: 'Waiting for approval.' },
    APPROVED: { label: 'Approved', severity: 'success', tooltip: 'Approval completed.' },
    CONFIRMED: { label: 'Confirmed', severity: 'success', tooltip: 'Confirmed order; direct edit is locked.' },
    ON_HOLD: { label: 'On hold', severity: 'warn', tooltip: 'Order is currently on hold.' },
    RELEASED: { label: 'Released', severity: 'info', tooltip: 'Order is released for downstream work.' },
    PARTIALLY_FULFILLED: { label: 'Partially fulfilled', severity: 'warn', tooltip: 'Some order lines are fulfilled.' },
    FULFILLED: { label: 'Fulfilled', severity: 'success', tooltip: 'Order fulfillment is complete.' },
    PARTIALLY_RECEIVED: { label: 'Partially received', severity: 'warn', tooltip: 'Some ordered quantity has been received.' },
    RECEIVED: { label: 'Received', severity: 'success', tooltip: 'Receiving is complete.' },
    PARTIALLY_INVOICED: { label: 'Partially invoiced', severity: 'warn', tooltip: 'Some order value has been invoiced.' },
    INVOICED: { label: 'Invoiced', severity: 'success', tooltip: 'Order has moved into invoicing.' },
    PARTIALLY_BILLED: { label: 'Partially billed', severity: 'warn', tooltip: 'Some purchase order value has been billed.' },
    BILLED: { label: 'Billed', severity: 'success', tooltip: 'Vendor billing is complete.' },
    CANCELLED: { label: 'Cancelled', severity: 'danger', tooltip: 'Cancelled order.' },
    CLOSED: { label: 'Closed', severity: 'secondary', tooltip: 'Closed order.' },
    REVISED: { label: 'Revised', severity: 'info', tooltip: 'Order has been revised.' },
    NOT_REQUIRED: { label: 'No approval required', severity: 'secondary', tooltip: 'Approval is not required for this order.' },
    REJECTED: { label: 'Rejected', severity: 'danger', tooltip: 'Approval was rejected.' },
    NOT_STARTED: { label: 'Not started', severity: 'secondary', tooltip: 'No downstream activity has started.' },
    NOT_INVOICED: { label: 'Not invoiced', severity: 'secondary', tooltip: 'No invoice has been generated.' },
    NOT_RECEIVED: { label: 'Not received', severity: 'secondary', tooltip: 'No receipt has been recorded.' },
    NOT_BILLED: { label: 'Not billed', severity: 'secondary', tooltip: 'No vendor bill has been matched.' },
    READY_TO_BILL: { label: 'Ready to bill', severity: 'info', tooltip: 'Purchase order lines are ready for vendor bill creation.' },
    BILL_DRAFTED: { label: 'Bill drafted', severity: 'info', tooltip: 'A vendor bill draft is ready for matching, approval, and posting.' },
    MATCHING: { label: 'Matching', severity: 'info', tooltip: 'Vendor bill matching is in progress.' },
    MATCHED: { label: 'Matched', severity: 'success', tooltip: 'Purchase order, receipt, and bill values match.' },
    MATCH_EXCEPTION: { label: 'Match exception', severity: 'danger', tooltip: 'Matching failed or needs exception approval.' },
    CLEAR: { label: 'Clear', severity: 'success', tooltip: 'No hold or credit issue is active.' },
    WARNING: { label: 'Warning', severity: 'warn', tooltip: 'Review warning before continuing.' },
    BLOCKED: { label: 'Blocked', severity: 'danger', tooltip: 'Action is blocked until resolved.' },
    OVERRIDE_APPROVED: { label: 'Override approved', severity: 'success', tooltip: 'Override approval was supplied.' }
};

export function normalizeOrderStatus(status: string | undefined | null): string {
    if (!status || !status.trim()) {
        return 'DRAFT';
    }
    const value = status.trim().toUpperCase();
    if (value === 'CONFIRM') {
        return 'CONFIRMED';
    }
    if (value === 'INVOICES') {
        return 'INVOICED';
    }
    return value;
}

export function getOrderStatusMetadata(status: string | undefined | null): OrderStatusMetadata {
    const normalized = normalizeOrderStatus(status);
    return STATUS_METADATA[normalized] ?? {
        label: normalized.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase()),
        severity: 'secondary',
        tooltip: normalized.replace(/_/g, ' ')
    };
}

export function isDraftOrderStatus(status: string | undefined | null): boolean {
    return normalizeOrderStatus(status) === 'DRAFT';
}

export function isConfirmedOrderStatus(status: string | undefined | null): boolean {
    const normalized = normalizeOrderStatus(status);
    return normalized === 'CONFIRMED' || normalized === 'INVOICED' || normalized === 'CLOSED';
}
